import React from 'react';
import { Paper, Box, Skeleton, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

/**
 * @component AIInsightSkeleton
 * @description Loading skeleton for AI insight panel
 * @returns {JSX.Element}
 */
export function AIInsightSkeleton() {
  return (
    <Paper
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Icon and headline skeleton */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Skeleton variant="circular" width={32} height={32} />
        <Skeleton variant="text" width="70%" height={28} />
      </Box>

      {/* Body text skeleton */}
      <Box sx={{ mb: 2 }}>
        <Skeleton variant="text" width="100%" height={20} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="85%" height={20} />
      </Box>

      {/* Metric skeleton */}
      <Box sx={{ mt: 'auto', mb: 1.5 }}>
        <Skeleton variant="text" width="40%" height={16} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="60%" height={24} />
      </Box>

      {/* Overlay with "Performing AI Analysis" */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1,
          bgcolor: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(1px)',
          pb: 8, // Padding bottom to avoid footer overlap
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5,
            backgroundColor: 'background.paper',
            p: 1,
            borderRadius: 5,
          }}
        >
          <AutoAwesomeIcon
            sx={{
              fontSize: 32,
              color: 'primary.main',
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': {
                  opacity: 1,
                },
                '50%': {
                  opacity: 0.6,
                },
              },
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              fontWeight: 500,
            }}
          >
            Performing AI Analysis
          </Typography>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          mt: 'auto',
          pt: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'left',
          gap: 0.5,
          position: 'relative',
          zIndex: 2, // Above overlay
          bgcolor: 'background.paper',
        }}
      >
        <AutoAwesomeIcon
          sx={{
            fontSize: 14,
            color: 'text.disabled',
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
            fontSize: '0.7rem',
            fontStyle: 'italic',
          }}
        >
          AI Insights powered by CANai
        </Typography>
      </Box>
    </Paper>
  );
}

